// React
import { useContext, useState } from 'react'
// Routing
import { useHistory } from 'react-router-dom'
// MainStem - UI
import { theme, Tooltip } from '@mainstem/react-mainstem'
// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faExternalLinkAlt, faFlask } from '@fortawesome/pro-light-svg-icons'
// Global - Components
import { DrawerQuickRFQ } from 'components'
// Global - Config
import { copilotURL } from 'config'
// Global - Context
import { AppContext, AppContextDetails } from 'context'
// Global - Helpers
import { permissionCheck } from 'helpers'
// Local - Components
import { SearchLive } from './components'
import { getBetaAppUrl } from './getBetaAppUrl'

// Subtle nav button component for header
const NavButton = ({
  children,
  disabled,
  href,
  icon,
  onClick
}: {
  children: React.ReactNode
  disabled?: boolean
  href?: string
  icon?: any
  onClick?: () => void
}) => {
  const className = `
        inline-flex items-center gap-2 px-3 py-1.5
        text-sm font-medium rounded-lg
        transition-colors duration-150
        focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-1
        ${
          disabled
            ? 'text-slate-400 cursor-not-allowed opacity-60'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200'
        }
      `
  const content = (
    <>
      {icon && <FontAwesomeIcon icon={icon} className={disabled ? 'text-slate-300' : 'text-slate-400'} />}
      <span className='whitespace-nowrap'>{children}</span>
    </>
  )

  if (href) {
    return (
      <a className={`${className} no-underline`} href={href}>
        {content}
      </a>
    )
  }

  return (
    <button disabled={disabled} onClick={disabled ? undefined : onClick} className={className}>
      {content}
    </button>
  )
}

// Icon-only nav button
const NavIconButton = ({
  icon,
  onClick,
  'aria-label': ariaLabel
}: {
  icon: any
  onClick?: () => void
  'aria-label': string
}) => {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className='
        inline-flex items-center justify-center w-8 h-8
        text-slate-500
        rounded-lg
        hover:bg-slate-100 hover:text-slate-700
        active:bg-slate-200
        transition-colors duration-150
        focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-1
      '
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  )
}

const NavTopButtons: React.FC = () => {
  // Routing
  const history = useHistory()
  // Global - Context
  const { loggedInUser, userToken } = useContext<AppContextDetails>(AppContext)
  const accessRFQ = permissionCheck(loggedInUser, 'Order', 'RFQ', 'true')
  const betaUrl = loggedInUser?.allowBeta && userToken ? getBetaAppUrl(window.location.hostname, userToken) : null
  const [showDrawerQuickRFQ, setShowDrawerQuickRFQ] = useState(false)

  const handleGoBack = () => {
    history.goBack()
  }

  return (
    <div className='flex items-center gap-1'>
      {/* Go Back Button */}
      <NavButton icon={faArrowLeft} onClick={handleGoBack}>
        Go Back
      </NavButton>

      {/* Divider */}
      <div className='w-px h-5 bg-slate-200 mx-1' />

      {/* Quick RFQ Button */}
      {accessRFQ ? (
        <NavButton icon={theme.icons.mainstem.rfq} onClick={() => setShowDrawerQuickRFQ(true)}>
          Quick RFQ™
        </NavButton>
      ) : (
        <Tooltip content='You do not currently have permission to access RFQs. Please contact your supervisor to request access.'>
          <span className='inline-flex'>
            <NavButton disabled icon={theme.icons.mainstem.rfq}>
              Quick RFQ™
            </NavButton>
          </span>
        </Tooltip>
      )}

      {betaUrl && (
        <NavButton href={betaUrl} icon={faFlask}>
          MainStem 2.0
        </NavButton>
      )}

      {/* Search */}
      <div className='ml-2 min-w-[450px] w-full'>
        <SearchLive />
      </div>

      {/* Quick RFQ Drawer */}
      {showDrawerQuickRFQ && (
        <DrawerQuickRFQ
          onClose={() => {
            setShowDrawerQuickRFQ(false)
          }}
        />
      )}
    </div>
  )
}

export default NavTopButtons
